/**
 * PlayerPotView for Poker Reborn
 *
 * Displays the current bet amount next to a player's seat.
 * Chips animate from here to the TablePotView when betting rounds end.
 *
 * @see docs/screens/room/PLAYER_POT.md - Player pot specification
 * @see docs/screens/room/CHIPS_DISPLAY.md - Animated chip display
 * @see docs/screens/room/ROOM_HIERARCHY.md - Layer structure
 */

import { Container, Graphics } from 'pixi.js';
import { Chips } from '../chips';
import { ChipsDisplay } from '@/components/common/ChipsDisplay';
import { addOriginMarker, ORIGIN_COLORS } from '@/utils/OriginDebug';
import { getScaledValue } from '@/utils/responsive';

// =============================================================================
// BBS BREAKPOINTS
// =============================================================================

/**
 * BBS breakpoints for PlayerPotView dimensions.
 * Uses discrete mode (values jump at breakpoints).
 *
 * Breakpoints: 375 (phone), 720 (tablet), 1440 (desktop), 1920 (large desktop)
 */
const PLAYER_POT_BREAKPOINTS = {
  fontSize:    { 375: 12, 720: 14, 1440: 17, 1920: 17 },
  pillHeight:  { 375: 18, 720: 20, 1440: 24, 1920: 24 },
  paddingX:    { 375: 8,  720: 10, 1440: 12, 1920: 12 },
  minWidth:    { 375: 36, 720: 42, 1440: 50, 1920: 50 },
  pillRadius:  { 375: 8,  720: 10, 1440: 12, 1920: 12 },
  chipOffset:  { 375: 8,  720: 10, 1440: 12, 1920: 12 },
  charWidth:   { 375: 6,  720: 7,  1440: 8,  1920: 8  },
};

// =============================================================================
// STYLES
// =============================================================================

const COLORS = {
  /** Background pill */
  background: {
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
  },

  /** Amount display colors */
  amount: {
    color: 0x00ff00,        // Green for bets
    increaseColor: 0x00ff00, // Green (same, bets always "increase")
    decreaseColor: 0x00ff00, // Green (bets don't decrease mid-hand)
  },
};

// =============================================================================
// PLAYER POT VIEW
// =============================================================================

export class PlayerPotView extends Container {
  private seatNumber: number;
  private background: Graphics;
  private amountDisplay: ChipsDisplay;
  private chips: Chips;

  // Effect injection points
  private potGlow: Graphics;
  private particleAnchor: Container;

  private currentBet = 0;
  private currentScreenWidth = 1920;

  /** Value currently shown in the display (may differ from currentBet during animation) */
  private _displayedBet = 0;

  /** When true, display updates are deferred until chips arrive */
  private _animationControlled = false;

  constructor(seatNumber: number) {
    super();
    this.seatNumber = seatNumber;

    // Create glow (behind everything, for effects)
    this.potGlow = new Graphics();
    this.potGlow.label = 'playerPotGlow';
    this.potGlow.visible = false;
    this.addChild(this.potGlow);

    // Create chips component (visual chip stack)
    this.chips = new Chips({ size: 'small', maxChips: 7, maxChipsRow2: 11, maxChipsRow3: 13 });
    this.chips.label = 'chipsContainer';
    // Set default position (to the left of the label pill)
    // This ensures flying chips have a valid target before layout() is called
    this.chips.x = -this.bbs(PLAYER_POT_BREAKPOINTS.minWidth) / 2 - this.bbs(PLAYER_POT_BREAKPOINTS.chipOffset);
    this.chips.y = this.bbs(PLAYER_POT_BREAKPOINTS.pillHeight) / 2;
    this.addChild(this.chips);

    // Create background pill (hidden by default)
    this.background = new Graphics();
    this.background.visible = false;
    this.addChild(this.background);

    // Create animated amount display (left-aligned within pill)
    this.amountDisplay = new ChipsDisplay({
      color: COLORS.amount.color,
      increaseColor: COLORS.amount.increaseColor,
      decreaseColor: COLORS.amount.decreaseColor,
      fontSize: this.bbs(PLAYER_POT_BREAKPOINTS.fontSize),
      prefix: '$',
      anchorX: 0,
      anchorY: 0.5,
    });
    this.addChild(this.amountDisplay);

    // Create particle anchor (for chip fly animations)
    this.particleAnchor = new Container();
    this.particleAnchor.label = 'playerPotParticleAnchor';
    this.addChild(this.particleAnchor);

    // Debug: Add origin marker if ?showOrigins=true
    addOriginMarker(this, ORIGIN_COLORS.playerPot);

    // Start hidden
    this.visible = false;
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Get seat number this pot belongs to
   */
  getSeatNumber(): number {
    return this.seatNumber;
  }

  /**
   * Set bet amount
   */
  setBet(amount: number): void {
    this.currentBet = amount;

    if (amount > 0) {
      // If animation-controlled, only store the value - display updates when chips arrive
      if (this._animationControlled) {
        return;
      }
      // Animate if this is an increase from existing displayed bet
      const shouldAnimate = this._displayedBet > 0 && amount > this._displayedBet;
      this.amountDisplay.setValue(amount, shouldAnimate);
      this.chips.setValue(amount);
      this._displayedBet = amount;
      this.visible = true;
      this.layout();
    } else {
      this.amountDisplay.setValue(0, false);
      this.chips.setValue(0, false);
      this._displayedBet = 0;
      this.visible = false;
      this._animationControlled = false; // Release control when cleared
    }
  }

  /**
   * Add to current bet amount
   *
   * @param delta - Amount to add
   * @param animated - Whether to animate the change (default: true)
   */
  addAmount(delta: number, animated = true): void {
    const newAmount = this.currentBet + delta;
    this.currentBet = newAmount;

    if (newAmount > 0) {
      this.amountDisplay.setValue(newAmount, animated);
      this.chips.setValue(newAmount, animated);
      this._displayedBet = newAmount;
      this.visible = true;
      this.layout();
    } else {
      this.amountDisplay.setValue(0, false);
      this.chips.setValue(0, false);
      this._displayedBet = 0;
      this.visible = false;
    }
  }

  /**
   * Get current bet amount
   */
  getBet(): number {
    return this.currentBet;
  }

  /**
   * Clear bet display
   */
  clear(): void {
    this.setBet(0);
  }

  /**
   * Resize for different screen widths (BBS).
   * @param screenWidth - Current screen width in pixels
   */
  resize(screenWidth: number): void {
    if (screenWidth === this.currentScreenWidth) return;
    this.currentScreenWidth = screenWidth;

    // Update chips BBS
    this.chips.resize(screenWidth);

    // Update amount display font size
    this.amountDisplay.setFontSize(this.bbs(PLAYER_POT_BREAKPOINTS.fontSize));

    // Recalculate layout if visible
    if (this.visible) {
      this.layout();
    }
  }

  // ===========================================================================
  // BBS HELPER
  // ===========================================================================

  /**
   * Get a BBS value for current screen width (discrete mode).
   */
  private bbs(breakpoints: Record<number, number>): number {
    return getScaledValue(this.currentScreenWidth, breakpoints, { discrete: true });
  }

  // ===========================================================================
  // ANIMATION VISIBILITY CONTROL
  // ===========================================================================

  /**
   * Defer display updates until chips arrive.
   * Call this before starting any chip animation.
   * The current display value stays visible; new values are applied in showAfterAnimation().
   */
  deferDisplayUpdates(): void {
    this._animationControlled = true;
  }

  /**
   * Hide the pot for animation purposes.
   * While hidden for animation, state updates won't auto-show it.
   * Used during blind posting animation so pot appears when chips arrive.
   */
  hideForAnimation(): void {
    this._animationControlled = true;
    this.visible = false;
  }

  /**
   * Show the pot after animation completes.
   * Releases animation control back to state and applies deferred display values.
   */
  showAfterAnimation(): void {
    this._animationControlled = false;
    // Apply deferred bet value to display (chips have "arrived")
    if (this.currentBet > 0) {
      // Animate if this is an increase from existing displayed value (raise scenario)
      const shouldAnimate = this._displayedBet > 0 && this.currentBet > this._displayedBet;
      this.amountDisplay.setValue(this.currentBet, shouldAnimate);
      this.chips.setValue(this.currentBet);
      this._displayedBet = this.currentBet;
      this.visible = true;
      this.layout();
    }
  }

  /**
   * Check if pot is currently hidden for animation.
   */
  isHiddenForAnimation(): boolean {
    return this._animationControlled;
  }

  /**
   * Receive chips animation.
   * Called when flying chips arrive at this pot.
   *
   * @param _fromX - Source X position (unused, for interface)
   * @param _fromY - Source Y position (unused, for interface)
   * @param amount - Amount being added
   * @returns Promise that resolves when animation completes
   */
  async receiveChips(_fromX: number, _fromY: number, amount: number): Promise<void> {
    // Add the amount with animation
    this.addAmount(amount, true);

    // Small delay for the pulse animation to complete
    return new Promise((resolve) => setTimeout(resolve, 150));
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private layout(): void {
    const paddingX = this.bbs(PLAYER_POT_BREAKPOINTS.paddingX);
    const minWidth = this.bbs(PLAYER_POT_BREAKPOINTS.minWidth);
    const height = this.bbs(PLAYER_POT_BREAKPOINTS.pillHeight);
    const radius = this.bbs(PLAYER_POT_BREAKPOINTS.pillRadius);
    const chipOffset = this.bbs(PLAYER_POT_BREAKPOINTS.chipOffset);

    // Use a fixed width estimate since text may be animating
    const estimatedWidth = this.estimateTextWidth(this.currentBet);
    const width = Math.max(estimatedWidth + paddingX * 2, minWidth);

    // Pill is aligned with the lowest chip (bottom chip center at y = height/2)
    const pillCenterY = height / 2;

    // Draw background pill
    this.background.clear();
    this.background.roundRect(
      -width / 2,
      pillCenterY - height / 2,
      width,
      height,
      radius
    );
    this.background.fill({ color: COLORS.background.fill, alpha: 0.9 });
    this.background.setStrokeStyle({
      width: 1,
      color: COLORS.background.stroke,
    });
    this.background.stroke();

    // Position text left-aligned within the pill, vertically centered with pill
    this.amountDisplay.x = -width / 2 + paddingX;
    this.amountDisplay.y = pillCenterY;

    // Position chips to the left of background
    this.chips.x = -width / 2 - chipOffset;
    this.chips.y = pillCenterY; // Bottom chip aligns with pill center
  }

  /**
   * Estimate text width based on final value (not animated intermediate)
   */
  private estimateTextWidth(amount: number): number {
    const charWidth = this.bbs(PLAYER_POT_BREAKPOINTS.charWidth);
    let chars = 1; // $ prefix

    if (amount >= 1000000) {
      chars += 5; // e.g., "1.5M"
    } else if (amount >= 1000) {
      chars += 5; // e.g., "1.5K"
    } else if (amount >= 100) {
      chars += 3;
    } else if (amount >= 10) {
      chars += 2;
    } else {
      chars += 1;
    }

    return chars * charWidth;
  }

  // ===========================================================================
  // PUBLIC EFFECT INJECTION POINT ACCESSORS
  // ===========================================================================

  /** Get glow graphics (for highlight effects) */
  getPotGlow(): Graphics {
    return this.potGlow;
  }

  /** Get particle anchor (for chip fly animations) */
  getParticleAnchor(): Container {
    return this.particleAnchor;
  }

  /** Get chips component (for direct access) */
  getChips(): Chips {
    return this.chips;
  }

  /** Get amount display (for direct animation control) */
  getAmountDisplay(): ChipsDisplay {
    return this.amountDisplay;
  }

  /**
   * Get chips container (for chip animations)
   * @deprecated Use getChips() instead
   */
  getChipsContainer(): Container {
    return this.chips;
  }

  /** Get position for effect targeting */
  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  /**
   * Set glow effect
   * @param color - Glow color
   * @param intensity - Glow intensity (0-1)
   */
  setGlow(color: number, intensity: number): void {
    const glowSize = 15;

    this.potGlow.clear();

    if (intensity > 0) {
      this.potGlow.roundRect(
        -40 - glowSize,
        -15 - glowSize,
        80 + glowSize * 2,
        30 + glowSize * 2,
        this.bbs(PLAYER_POT_BREAKPOINTS.pillRadius) + glowSize / 2
      );
      this.potGlow.fill({ color, alpha: intensity * 0.4 });
      this.potGlow.visible = true;
    } else {
      this.potGlow.visible = false;
    }
  }
}
